import type {
  ObjectToolbarConfig,
  RegisterToolbarConfigArgs,
} from '@/blocks/object-toolbar';

/**
 * Assemble the object-toolbar config for the Oversight assessment results
 * (findings) register/list page (CUS-164). Register toolbars are type-level: no
 * `parentObject` (permissions resolve against the type) and no `save` (a list
 * page has no form). The register's multi-format export (CSV / PDF / custom PDF)
 * is surfaced as the toolbar's "Download" group — one action per `ExportOption`
 * returned by `useExportOptions`, the same source that drives the legacy
 * `ExportButton`. The results register has no create button, so it never
 * supplies `create` (export-only, mirroring the Indicators register).
 *
 * The register's bulk-delete (selection-driven, gated on a compound OR of the
 * per-result-type delete permissions) is NOT expressed here — it can't be
 * reduced to the shared args' single permission gate. `OversightResultRegisterToolbar`
 * augments this config's `danger` slot at the component level instead.
 *
 * Takes the shared {@link RegisterToolbarConfigArgs} (see
 * `blocks/object-toolbar/types.ts`). Pure so it can be unit-tested without
 * rendering the page.
 */
export const buildOversightResultRegisterToolbarConfig = ({
  title,
  counter,
  exportOptions,
  editRibbon,
}: RegisterToolbarConfigArgs): ObjectToolbarConfig => ({
  header: { title, counter },
  actions: {
    ...(editRibbon
      ? {
          overflow: [
            {
              id: 'edit',
              label: editRibbon.groupLabel,
              actions: [
                {
                  id: 'edit-ribbon',
                  label: editRibbon.label,
                  iconName: 'filter-lines',
                  onClick: editRibbon.onClick,
                },
              ],
            },
          ],
        }
      : {}),
    export: [
      {
        id: 'export',
        actions: exportOptions.map((option) => ({
          id: option.id,
          label: option.text,
          iconName: 'download-01',
          onClick: option.exportFunction,
          disabled: option.disabled,
        })),
      },
    ],
  },
});
